import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { BreakManagementState, BreakSettings, BreakState, ExportRange, ForceStopTarget, SettingsDraft } from './types'

export const initialState: BreakManagementState = {
  breakState: null,
  clockOffsetMs: 0,
  lastWriteAt: 0,
  isSubmitting: false,
  isLimitModalOpen: false,
  forceStopTarget: null,
  settingsLoaded: false,
  settingsError: null,
  settingsDraft: { maxWeekday: '', maxSaturday: '' },
  isSavingSettings: false,
  isExporting: false,
  exportRange: { start: null, end: null },
  error: null,
}

interface FetchStateSuccessPayload {
  breakState: BreakState
  requestedAt: number
  receivedAt: number
}

const fetchStateSuccessHandler = (state: BreakManagementState, action: PayloadAction<FetchStateSuccessPayload>) => {
  const { breakState, requestedAt, receivedAt } = action.payload
  if (requestedAt < state.lastWriteAt) return

  state.breakState = breakState
  state.clockOffsetMs = Date.parse(breakState.server_time) - receivedAt
  state.error = null
}

const fetchStateFailureHandler = (state: BreakManagementState, action: PayloadAction<string>) => {
  state.error = action.payload
}

const setSubmittingHandler = (state: BreakManagementState, action: PayloadAction<boolean>) => {
  state.isSubmitting = action.payload
}

const markWriteHandler = (state: BreakManagementState, action: PayloadAction<number>) => {
  state.lastWriteAt = action.payload
}

const setForceStopTargetHandler = (state: BreakManagementState, action: PayloadAction<ForceStopTarget | null>) => {
  state.forceStopTarget = action.payload
}

const setLimitModalOpenHandler = (state: BreakManagementState, action: PayloadAction<boolean>) => {
  state.isLimitModalOpen = action.payload
}

const fetchSettingsSuccessHandler = (state: BreakManagementState, action: PayloadAction<BreakSettings>) => {
  state.settingsLoaded = true
  state.settingsDraft = {
    maxWeekday: String(action.payload.max_weekday),
    maxSaturday: String(action.payload.max_saturday),
  }
  state.settingsError = null
}

const setSettingsDraftHandler = (state: BreakManagementState, action: PayloadAction<Partial<SettingsDraft>>) => {
  state.settingsDraft = { ...state.settingsDraft, ...action.payload }
}

const setExportRangeHandler = (state: BreakManagementState, action: PayloadAction<Partial<ExportRange>>) => {
  state.exportRange = { ...state.exportRange, ...action.payload }
}

const fetchSettingsFailureHandler = (state: BreakManagementState, action: PayloadAction<string>) => {
  state.settingsError = action.payload
}

const setSavingSettingsHandler = (state: BreakManagementState, action: PayloadAction<boolean>) => {
  state.isSavingSettings = action.payload
}

const setExportingHandler = (state: BreakManagementState, action: PayloadAction<boolean>) => {
  state.isExporting = action.payload
}

const clearBreakManagementHandler = () => initialState

export const breakManagementSlice = createSlice({
  name: 'breakManagement',
  initialState,
  reducers: {
    fetchStateSuccess: fetchStateSuccessHandler,
    fetchStateFailure: fetchStateFailureHandler,
    setSubmitting: setSubmittingHandler,
    markWrite: markWriteHandler,
    setForceStopTarget: setForceStopTargetHandler,
    setLimitModalOpen: setLimitModalOpenHandler,
    fetchSettingsSuccess: fetchSettingsSuccessHandler,
    fetchSettingsFailure: fetchSettingsFailureHandler,
    setSettingsDraft: setSettingsDraftHandler,
    setExportRange: setExportRangeHandler,
    setSavingSettings: setSavingSettingsHandler,
    setExporting: setExportingHandler,
    clearBreakManagement: clearBreakManagementHandler,
  },
})

export const {
  fetchStateSuccess,
  fetchStateFailure,
  setSubmitting,
  markWrite,
  setForceStopTarget,
  setLimitModalOpen,
  fetchSettingsSuccess,
  fetchSettingsFailure,
  setSettingsDraft,
  setExportRange,
  setSavingSettings,
  setExporting,
  clearBreakManagement,
} = breakManagementSlice.actions

export default breakManagementSlice.reducer
